"use client";

import { ImageIcon } from "@repo/ui/components/icons";
import { cn } from "@repo/ui/lib/utils";
import { useEffect, useRef, useState } from "react";

// The bar's photos (plan 2.13): one per view with swipe (scroll-snap), small
// thumbnails under it, and a tap opens the photo full size in a dialog.
export function ProductGallery({ images, title }: { images: { url: string; alt: string }[]; title: string }) {
  const trackRef = useRef<HTMLDivElement>(null);
  const slideRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [active, setActive] = useState(0);
  const [zoomed, setZoomed] = useState<number | null>(null);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) setActive(Number((entry.target as HTMLElement).dataset.index));
        }
      },
      { root: track, threshold: 0.6 },
    );
    for (const el of slideRefs.current) if (el) observer.observe(el);
    return () => observer.disconnect();
  }, [images.length]);

  useEffect(() => {
    if (zoomed === null) dialogRef.current?.close();
    else dialogRef.current?.showModal();
  }, [zoomed]);

  const goTo = (index: number) => {
    const track = trackRef.current;
    const target = slideRefs.current[index];
    if (!track || !target) return;
    // RTL: a slide's "start" is its right edge (scrollLeft is negative in RTL).
    track.scrollBy({ left: target.getBoundingClientRect().right - track.getBoundingClientRect().right, behavior: "smooth" });
  };

  if (images.length === 0) {
    return (
      <div className="bg-background flex aspect-square items-center justify-center rounded-card shadow-sm">
        <ImageIcon size={40} className="text-muted-foreground opacity-50" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div
        ref={trackRef}
        className="bg-background flex aspect-square snap-x snap-mandatory overflow-x-auto rounded-card shadow-sm [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {images.map((image, index) => (
          <button
            key={image.url}
            type="button"
            ref={(el) => {
              slideRefs.current[index] = el;
            }}
            data-index={index}
            onClick={() => setZoomed(index)}
            aria-label={`نمایش بزرگ تصویر ${(index + 1).toLocaleString("fa-IR")}`}
            className="relative shrink-0 basis-full cursor-zoom-in snap-start"
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={image.url}
              alt={image.alt || title}
              loading={index === 0 ? "eager" : "lazy"}
              fetchPriority={index === 0 ? "high" : undefined}
              className="absolute inset-0 h-full w-full object-cover"
            />
          </button>
        ))}
      </div>

      {images.length > 1 ? (
        <div className="flex gap-2">
          {images.map((image, index) => (
            <button
              key={image.url}
              type="button"
              onClick={() => goTo(index)}
              aria-label={`تصویر ${(index + 1).toLocaleString("fa-IR")}`}
              aria-current={active === index}
              className={cn(
                "bg-background relative h-16 w-16 overflow-hidden rounded-xl shadow-sm transition-opacity",
                active === index ? "ring-foreground ring-2" : "opacity-60 hover:opacity-100",
              )}
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={image.url} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
            </button>
          ))}
        </div>
      ) : null}

      <dialog
        ref={dialogRef}
        onClose={() => setZoomed(null)}
        onClick={() => setZoomed(null)}
        className="m-auto max-h-[90svh] max-w-[min(90vw,900px)] overflow-hidden rounded-card p-0 backdrop:bg-black/70"
      >
        {zoomed !== null && images[zoomed] ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={images[zoomed].url} alt={images[zoomed].alt || title} className="block max-h-[90svh] w-auto" />
        ) : null}
      </dialog>
    </div>
  );
}
